import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { Button, Card } from '@safermark/shared/ui';
import { apiClient } from '@safermark/shared/api';
import { useAuth } from '../hooks/useAuth';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  
  // Prevent double API calls in React StrictMode
  const hasVerified = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Verification token is missing.');
      return;
    }

    // Prevent double API calls in React StrictMode
    if (hasVerified.current) {
      return;
    }

    // Mark as verified to prevent duplicate calls
    hasVerified.current = true;

    // Verify the email
    const verifyEmail = async () => {
      try {
        console.log('Verifying email with token:', token.substring(0, 8) + '...');
        const response = await apiClient.verifyEmail(token);
        setStatus('success');
        setMessage(response.message || 'Email verified successfully!');
        
        // Refresh user data if user is logged in to update emailVerified status
        if (isAuthenticated) {
          try {
            await refreshUser(true); // Force refresh after email verification
            console.log('User data refreshed after email verification');
          } catch (refreshError) {
            console.warn('Failed to refresh user data:', refreshError);
            // Don't fail the verification if refresh fails
          }
        }
      } catch (error) {
        console.error('Email verification failed:', error);
        setStatus('error');
        setMessage(
          error instanceof Error 
            ? error.message 
            : 'Email verification failed. The token may be invalid or expired.'
        );
      }
    };

    verifyEmail();
  }, [searchParams, isAuthenticated, refreshUser]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full p-8">
        <div className="text-center">
          {status === 'loading' && (
            <>
              <Loader className="mx-auto h-12 w-12 text-blue-600 animate-spin mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verifying Email
              </h2>
              <p className="text-gray-600">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="mx-auto h-12 w-12 text-green-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Email Verified!
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Continue to Login
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => navigate('/dashboard')}
                  className="w-full"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="mx-auto h-12 w-12 text-red-600 mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-gray-600 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/register')}
                  className="w-full"
                >
                  Back to Registration
                </Button>
                <Button 
                  variant="secondary"
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Go to Login
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}